import { useCallback } from 'react';
import type {
  EnvironmentKey,
  ConfigEntity,
  DomainEntity,
  EntityRootData,
  ConfigEntityRootData,
} from '@web-config-app/core';
import { isConfigEntity } from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { useStore } from 'zustand';
import {
  UseEntityGetOptions,
  useEntityGet,
} from '../use-entity-get/use-entity-get';
import type { UseEntityUpdateOptions } from '../use-entity-update/use-entity-update';
import { UseEntityCreateOptions } from '../use-entity-create/use-entity-create';
import { useEntityEnvironmentSync } from '../use-entity-environment-sync/use-entity-environment-sync';
import { useCopyEntitiesToEnvironment } from '../use-copy-entities-to-environment/use-copy-entities-to-environment';
import { isConfigEntityDetail } from '../../utilities/is-config-entity-detail/is-config-entity-detail';
import type {
  CopyEntitiesToEnvironmentSuccessData,
  CopyTargetOverride,
} from '../use-copy-entities-to-environment/use-copy-entities-to-environment';
import { type BulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';

export interface UseMultiEnvironmentEntityOperationsProps {
  entity: ConfigEntity | DomainEntity;
  entityData: EntityRootData | undefined;
  isUsingConfigAppBackend?: boolean;
  instanceId: string;
  options?: {
    get?: UseEntityGetOptions;
    copyToEnvironment?: UseEntityCreateOptions<{
      data: CopyEntitiesToEnvironmentSuccessData;
    }>;
    syncToEnvironment?: UseEntityUpdateOptions;
  };
  /**
   * The tenant ID to capture errors with.
   * @param tenantId - The tenant ID to capture errors with.
   */
  tenantId: string;
  targetEnvironment: EnvironmentKey;
  targetEnvironmentName?: string;
  sourceEnvironment: EnvironmentKey;
  /**
   * The domain entity type (e.g. 'activity'). Used for the bulk action
   * reporting modal title.
   */
  domainEntityType?: string;
  /**
   * Optional external bulk action reporting store. When provided the hook
   * writes to this store instead of creating its own, allowing a single
   * store (and a single modal) to be shared across operations on the
   * details page.
   */
  bulkActionReportingStore: BulkActionReportingStore;
}

export type UseMultiEnvironmentEntityOperationsResult = ReturnType<
  typeof useMultiEnvironmentEntityOperations
>;

/**
 * A custom hook that manages operations for entities across multiple environments.
 * It handles fetching entity data, copying entities between environments, and ensures referenced assets
 * are properly synchronized between environments before copying.
 *
 * @param props - The properties for multi-environment entity operations
 * @param props.entity - The entity to perform operations on
 * @param props.instanceId - The unique identifier for the entity instance
 * @param props.entityData - The root data of the entity
 * @param props.options - Optional configuration for get and copy operations
 * @param props.options.get - {@link  UseEntityGetOptions} Options for entity retrieval
 * @param props.options.copyToEnvironment - {@link UseEntityCreateOptions} Options for copying entity to target environment
 * @param props.targetEnvironment - The environment ID where the entity will be copied to
 * @param props.sourceEnvironment - The environment ID where the entity is being copied from
 *
 * @returns An object containing:
 * - entityStatus: The current status of the entity in the target environment
 * - copyToEnvironment: A function that synchronizes assets and copies the entity to the target environment
 *
 * @example
 * const { entityStatus, copyToEnvironment } = useMultiEnvironmentEntityOperations({
 *   entity,
 *   instanceId,
 *   entityData,
 *   targetEnvironment: 'production',
 *   sourceEnvironment: 'staging'
 * });
 */

export const useMultiEnvironmentEntityOperations = ({
  entity,
  instanceId,
  entityData,
  isUsingConfigAppBackend,
  options,
  targetEnvironment,
  targetEnvironmentName,
  sourceEnvironment,
  domainEntityType,
  tenantId,
  bulkActionReportingStore,
}: UseMultiEnvironmentEntityOperationsProps) => {
  const { formatMessage } = useIntl();

  const bulkActionReportingSession = useStore(
    bulkActionReportingStore,
    (state) => state.session,
  );

  const clearBulkActionReportingSession = useCallback(() => {
    bulkActionReportingStore.getState().clearSession();
  }, [bulkActionReportingStore]);

  const get = useEntityGet({
    entity: isConfigEntity(entity) ? entity.domainEntity : entity,
    instanceId,
    environment: targetEnvironment,
    options: {
      getIncludedEntities: true,
      isUsingConfigAppBackend,
      retry: false,
      ...options?.get,
    },
  });

  /**
   * Disabling a query stops new fetches; it doesn't hide data an earlier mount already
   * cached for that key. Masking here, once, means no caller can read a payload describing an
   * environment this hook isn't pointed at -- and a field added below can't escape the mask.
   */
  const isGetEnabled = options?.get?.enabled ?? true;
  const targetEnvironmentData = isGetEnabled ? get.data : undefined;

  const { copyEntitiesToEnvironment } = useCopyEntitiesToEnvironment({
    sourceEnvironment,
    targetEnvironment,
    targetEnvironmentName: targetEnvironmentName ?? targetEnvironment,
    // Always pass the raw domain schema (domainEntity.schema) — not the
    // pre-merged configEntity.schema — so getAllReferencedEntities can
    // correctly merge it to match the API data format without double-nesting.
    schema: isConfigEntity(entity) ? entity.domainEntity.schema : entity.schema,
    domainEntityType: domainEntityType ?? entity.type,
    tenantId,
    bulkActionReportingStore,
  });

  /**
   * Copy the entity to `target`, defaulting to the configured environment.
   *
   * An argument rather than hook state so a caller with several targets can act on whichever
   * the author just clicked, in the same tick. Sync can't work this way -- it needs the
   * target's current data to build its payload and pick update vs republish.
   */
  const handleCopyEntityToEnvironment = useCallback(
    async (target?: CopyTargetOverride) => {
      const resolvedTarget = target?.environmentKey ?? targetEnvironment;
      if (isConfigEntityDetail(entityData)) {
        const data = await copyEntitiesToEnvironment([entityData], target);

        // Check if the root entity failed to promote
        const rootEntityResult = data.configEntities.find(
          (entityIndexedDBEntry) => entityIndexedDBEntry.id === entityData.id,
        );
        if (rootEntityResult?.status === 'error') {
          // call the onError callback
          options?.copyToEnvironment?.onError?.(
            new Error(rootEntityResult?.error ?? 'Unknown error'),
            undefined,
            undefined,
          );
          return;
        }

        // Reports where the copy actually went, so the caller invalidates that
        // environment's cache rather than the configured target's.
        options?.copyToEnvironment?.onSuccess?.({ data }, resolvedTarget);
      } else {
        // This branch feels unlikely to run but if it does, throwing an
        // error feels appropriate.
        options?.copyToEnvironment?.onError?.(
          new Error(
            formatMessage({
              id: 'COPY_TO_ENVIRONMENT_UNSUPPORTED_ENTITY_DATA_TYPE',
            }),
          ),
          undefined,
          undefined,
        );
      }
    },
    [
      copyEntitiesToEnvironment,
      entityData,
      options?.copyToEnvironment,
      formatMessage,
      targetEnvironment,
    ],
  );

  const { handleEnvironmentSync } = useEntityEnvironmentSync({
    configEntity: entity as ConfigEntity,
    instanceId,
    currentEnvironmentData: entityData as ConfigEntityRootData,
    otherEnvironmentData: targetEnvironmentData as ConfigEntityRootData,
    otherEnvironmentKey: targetEnvironment,
    options: options?.syncToEnvironment,
  });

  return {
    entityStatus: targetEnvironmentData?.attributes?.entityMetadata?.status,
    entityData: targetEnvironmentData,
    /**
     * Whether the target lookup settled, resolved or failed. Separates "not there" from
     * "haven't looked", which `entityData` alone can't: both are undefined.
     */
    isEntityDataFetched: isGetEnabled && get.isFetched,
    syncToEnvironment: handleEnvironmentSync,
    copyEntitiesToEnvironment: handleCopyEntityToEnvironment,
    readOnly: targetEnvironmentData?.attributes?.entityMetadata?.readOnly,
    bulkActionReportingSession,
    clearBulkActionReportingSession,
  };
};
